import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  FormControl,
  Snackbar,
  MenuItem,
  DialogActions,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTranslation } from 'react-i18next';
import { Backup } from '@mui/icons-material';
import type { GenericDocumentType } from '../../data/models/document-type.model';
import validationUtils from '../../services/validationUtils';
import type { GenericDocument } from '../../data/models/document.model';
import { useIsALUser } from '../../hooks/useIsInternalUser';
import { DateTime } from 'luxon';

const PREFIX = 'DocumentDialog';

const classes = {
  uploadButton: `${PREFIX}-uploadButton`,
  form: `${PREFIX}-form`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.uploadButton}`]: {
    marginBottom: theme.spacing(1),
  },

  [`& .${classes.form}`]: {
    margin: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface DocumentDialogProps {
  open: boolean;
  documentTypes: GenericDocumentType[];
  documentType?: number;
  document?: GenericDocument;
  close: (
    file: any,
    typeId: number,
    signedOn: string | null,
    effectiveStart: string | null,
    effectiveEnd: string | null,
  ) => void;
}

export default function DocumentDialog({
  open,
  documentTypes,
  documentType,
  document,
  close,
}: DocumentDialogProps) {
  const { t } = useTranslation('common');

  const [File, setFile] = React.useState<any>(null);
  const [DocumentTypeID, setDocumentTypeID] = React.useState(1);
  const [SignedOnDate, setSignedOnDate] = React.useState<any>(null);
  const [EffectiveStartDate, setEffectiveStartDate] = React.useState<any>(null);
  const [EffectiveEndDate, setEffectiveEndDate] = React.useState<any>(null);
  const [HasSignedDateError, setHasSignedDateError] = React.useState(false);
  const [HasStartDateError, setHasStartDateError] = React.useState(false);
  const [HasEndDateError, setHasEndDateError] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const isAlUser = useIsALUser();

  React.useEffect(() => {
    if (documentType !== undefined) {
      setDocumentTypeID(documentType);
    }
  }, [documentType]);

  React.useEffect(() => {
    if (document) {
      setSignedOnDate(
        document?.signedOnDate ? DateTime.fromISO(document.signedOnDate) : null,
      );
      setEffectiveStartDate(
        document.effectiveStartDate
          ? DateTime.fromISO(document.effectiveStartDate)
          : null,
      );
      setEffectiveEndDate(
        document.effectiveEndDate
          ? DateTime.fromISO(document.effectiveEndDate)
          : null,
      );
    }
  }, [document]);

  React.useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  const reset = () => {
    setFile(null);
    setDocumentTypeID(1);
    setSignedOnDate(null);
    setEffectiveStartDate(null);
    setEffectiveEndDate(null);
  };

  const areDatesRequired = (): boolean => {
    const curType = documentTypes.find(
      (dt) => dt.documentTypeID === DocumentTypeID,
    );
    return curType?.isEffectiveDatesRequired || false;
  };

  const saveDocument = (evt: any) => {
    evt.preventDefault();
    const signedOn = SignedOnDate
      ? SignedOnDate.toFormat(validationUtils.validDateFormat())
      : null;
    const start = EffectiveStartDate
      ? EffectiveStartDate.toFormat(validationUtils.validDateFormat())
      : null;
    const end = EffectiveEndDate
      ? EffectiveEndDate.toFormat(validationUtils.validDateFormat())
      : null;
    close(File, DocumentTypeID, signedOn, start, end);
  };

  const cancelDocumentDialog = () => {
    close(null, 0, null, null, null);
  };

  const onFileChange = (evt: any) => {
    const file = evt.target.files[0];
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    if (file && file.size > maxSize) {
      setSnackbarOpen(true);
      return;
    }
    setFile(evt.target.files[0]);
  };

  const isValid = (): boolean => {
    const hasRequired =
      !areDatesRequired() || (!!EffectiveStartDate && !!EffectiveEndDate);
    return (
      (File || document) &&
      DocumentTypeID &&
      !HasSignedDateError &&
      !HasStartDateError &&
      !HasEndDateError &&
      hasRequired
    );
  };

  return (
    <StyledDialog
      open={open}
      onClose={cancelDocumentDialog}
      maxWidth="sm"
      fullWidth={true}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity="error">
          {t('common.documentSizeError')}
        </Alert>
      </Snackbar>
      <DialogTitle>
        {t(document ? 'document.editDocument' : 'document.addDocument')}
      </DialogTitle>
      <DialogContent>
        <form>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Box className={classes.form}>
              <input
                type="file"
                id="fileUploadButton"
                name="document"
                hidden
                accept="*"
                onChange={onFileChange}
                disabled={!!document}
              />
              <label htmlFor={'fileUploadButton'}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.uploadButton}
                  component="span"
                  disabled={!!document}
                  startIcon={<Backup />}>
                  {File ? File.name : t('document.chooseFile')}
                </Button>
              </label>
              <FormControl>
                <TextField
                  select
                  required={true}
                  label={t('document.documentType')}
                  variant="filled"
                  value={DocumentTypeID}
                  disabled={documentType !== undefined}
                  onChange={(evt) => {
                    setDocumentTypeID(parseInt(evt.target.value, 10));
                  }}>
                  {documentTypes
                    ?.filter((t) => t.isActive)
                    .map((type) => {
                      return (
                        <MenuItem
                          value={type.documentTypeID}
                          key={type.documentTypeID}>
                          {type.documentTypeName}
                        </MenuItem>
                      );
                    })}
                </TextField>
              </FormControl>
              <DatePicker
                label={t('document.signedOn')}
                format="MM/dd/yyyy"
                value={SignedOnDate}
                slots={{
                  textField: TextField,
                }}
                slotProps={{
                  textField: {
                    variant: 'filled',
                  },
                }}
                onChange={(date) => setSignedOnDate(date)}
                onError={(err) => setHasSignedDateError(!!err)}
              />
              <DatePicker
                label={t('document.effectiveStart')}
                format="MM/dd/yyyy"
                value={EffectiveStartDate}
                maxDate={EffectiveEndDate || undefined}
                slots={{
                  textField: TextField,
                }}
                slotProps={{
                  textField: {
                    variant: 'filled',
                    required: areDatesRequired(),
                  },
                }}
                onChange={(date) => setEffectiveStartDate(date)}
                onError={(err) => setHasStartDateError(!!err)}
              />
              <DatePicker
                label={t('document.effectiveEnd')}
                format="MM/dd/yyyy"
                value={EffectiveEndDate}
                minDate={EffectiveStartDate || undefined}
                slots={{
                  textField: TextField,
                }}
                slotProps={{
                  textField: (ownerState) => ({
                    variant: 'filled',
                    required: areDatesRequired(),
                    helperText: ownerState.error
                      ? t('document.minDateMessage')
                      : null,
                  }),
                }}
                onChange={(date) => setEffectiveEndDate(date)}
                onError={(err) => setHasEndDateError(!!err)}
                disabled={!!document && !isAlUser}
              />
            </Box>
          </LocalizationProvider>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="contained"
          disabled={!isValid()}
          onClick={saveDocument}>
          {t('common.save')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={cancelDocumentDialog}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
